import { SettingsCard } from './SettingsCard';
// ----- Phase 5.X-settings-schema-ui — Section panel -----
//
// Renders one section header + the controls for every schema entry in
// that section. The page passes the per-entry value + change handler
// down; this component is a pure presenter.

import type { ReactElement } from 'react';
import type {
  SettingsSchemaEntry,
  SettingsSection,
} from '@/lib/settings-schema';
import { SettingToggleRow } from './SettingToggleRow';
import { SettingSliderRow } from './SettingSliderRow';
import { SettingSelectRow } from './SettingSelectRow';

export interface SettingsSectionPanelProps {
  readonly section: SettingsSection;
  readonly entries: ReadonlyArray<SettingsSchemaEntry>;
  /** Effective values by key (post 3-tier merge), with schema defaults filled in. */
  readonly values: Readonly<Record<string, boolean | number | string>>;
  /** True when the current user has Users:manage (per the SettingsPage gate). */
  readonly canWriteOrg: boolean;
  /** True when auth is loaded (false during pre-load to avoid flicker). */
  readonly canWriteUser: boolean;
  /** Called with (key, newValue) when a control changes. */
  readonly onChange: (key: string, next: boolean | number | string) => void;
}

function describeDisabled(
  entry: SettingsSchemaEntry,
  canWriteOrg: boolean,
  canWriteUser: boolean,
): { disabled: boolean; reason: string | undefined } {
  if (entry.level === 'org') {
    if (!canWriteOrg) {
      return {
        disabled: true,
        reason: 'Org admins only (requires Users:manage permission)',
      };
    }
    return { disabled: false, reason: undefined };
  }
  // user-level
  if (!canWriteUser) {
    return { disabled: true, reason: 'Sign-in required' };
  }
  return { disabled: false, reason: undefined };
}

export function SettingsSectionPanel({ section, ...fields }: SettingsSectionPanelProps): ReactElement {
  return (
    <SettingsCard
      title={section}
      description={`${fields.entries.length} setting${fields.entries.length === 1 ? '' : 's'}`}
      testId={`settings-section-${section}`}
    >
      <SettingsSectionFields {...fields} />
    </SettingsCard>
  );
}

/** Reuse the same editors inside a combined settings card without nesting another card. */
export function SettingsSectionFields({
  entries,
  values,
  canWriteOrg,
  canWriteUser,
  onChange,
}: Omit<SettingsSectionPanelProps, 'section'>): ReactElement {
  return (
    <>
      {entries.map((entry) => {
        const { disabled, reason } = describeDisabled(entry, canWriteOrg, canWriteUser);
        const showOrgLock = entry.level === 'org';
        const raw = values[entry.key];
        if (entry.controlType === 'toggle') {
          // Fall back to the schema's defaultValue when the page hasn't
          // resolved an effective value yet (e.g., initial render before
          // listAll() returns). Only coerce when the value is present.
          const v =
            typeof raw === 'boolean'
              ? raw
              : raw === undefined
                ? (entry.defaultValue as boolean)
                : Boolean(raw);
          return (
            <SettingToggleRow
              key={entry.key}
              entry={entry}
              value={v}
              showOrgLock={showOrgLock}
              disabled={disabled}
              {...(reason !== undefined && { disabledReason: reason })}
              onChange={(next) => onChange(entry.key, next)}
            />
          );
        }
        if (entry.controlType === 'select') {
          const v =
            typeof raw === 'string'
              ? raw
              : raw === undefined
                ? String(entry.defaultValue)
                : String(raw);
          return (
            <SettingSelectRow
              key={entry.key}
              entry={entry}
              value={v}
              showOrgLock={showOrgLock}
              disabled={disabled}
              {...(reason !== undefined && { disabledReason: reason })}
              onChange={(next) => onChange(entry.key, next)}
            />
          );
        }
        // slider
        const v = typeof raw === 'number' ? raw : Number(raw) || (entry.defaultValue as number);
        return (
          <SettingSliderRow
            key={entry.key}
            entry={entry}
            value={v}
            showOrgLock={showOrgLock}
            disabled={disabled}
            {...(reason !== undefined && { disabledReason: reason })}
            onChange={(next) => onChange(entry.key, next)}
          />
        );
      })}
    </>
  );
}
